// src/pages/VehicleV2/ServiceExpiryAlert.jsx

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaSave, FaCheckSquare, FaSquare, FaTimes } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/serviceExpiryAlert";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const defaultAlerts = {
  insurance: { enabled: false, days: 0, software: false, sms: false, email: false },
  fc: { enabled: false, days: 0, software: false, sms: false, email: false },
  roadTax: { enabled: false, days: 0, software: false, sms: false, email: false },
  permit: { enabled: false, days: 0, software: false, sms: false, email: false },
  periodicHr: { enabled: false, value: 0, software: false, sms: false, email: false },
  periodicKm: { enabled: false, value: 0, software: false, sms: false, email: false },
};

const ownVehicleRows = [
  { key: "insurance", label: "Insurance" },
  { key: "fc", label: "FC" },
  { key: "roadTax", label: "Road Tax" },
  { key: "permit", label: "Permit" },
];

const periodicRows = [
  { key: "periodicHr", label: "Periodic-HR" },
  { key: "periodicKm", label: "Periodic-KM" },
];

const ServiceExpiryAlert = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = toStr(
    localStorage.getItem("finYearId") ||
      localStorage.getItem("finyearid") ||
      localStorage.getItem("financialyearid")
  );
  const finYear = toStr(
    localStorage.getItem("finYear") ||
      localStorage.getItem("finyear") ||
      localStorage.getItem("financialyear")
  );
  const branchId = toStr(
    localStorage.getItem("branchId") || localStorage.getItem("branchid")
  );
  const branch = toStr(
    localStorage.getItem("branchType") || localStorage.getItem("branchtype")
  );
  const userId = toStr(
    localStorage.getItem("userId") ||
      localStorage.getItem("loginuserid") ||
      localStorage.getItem("loginUserId")
  );
  const loginUserId = toStr(
    sessionStorage.getItem("loginUserId") ||
      sessionStorage.getItem("userid") ||
      userId
  );

  const commonHeaders = useMemo(() => {
    const headers = {
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": loginUserId || userId,
    };
    if (finYear) headers["x-finyear"] = finYear;
    if (finYearId) {
      headers["x-finyearid"] = finYearId;
      headers["x-finyear-id"] = finYearId;
    }
    return headers;
  }, [finYearId, finYear, branchId, branch, userId, loginUserId]);

  const tenantParams = useMemo(
    () => ({
      loginUserId: loginUserId || userId,
      finYear,
      finYearId,
    }),
    [loginUserId, userId, finYear, finYearId]
  );

  const [alerts, setAlerts] = useState(defaultAlerts);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [tenantInfo, setTenantInfo] = useState("");

  const busy = pageLoading || loading;
  const selectAllUsers = users.length > 0 && users.every((u) => !!u.selected);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const Checkbox = ({ checked, onChange, disabled = false }) => (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`flex items-center justify-center w-5 h-5 ${
        disabled ? "opacity-60 cursor-not-allowed" : ""
      }`}
    >
      {checked ? (
        <FaCheckSquare className="w-5 h-5 text-blue-600" />
      ) : (
        <FaSquare className="w-5 h-5 text-gray-400" />
      )}
    </button>
  );

  const ToggleButton = ({ enabled, onChange, disabled = false }) => (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      disabled={disabled}
      className={`w-12 h-6 rounded-full transition-colors duration-200 flex items-center ${
        enabled ? `${buttonClass} justify-end` : "bg-gray-300 dark:bg-gray-600 justify-start"
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <div className="w-4 h-4 rounded-full bg-white mx-1" />
    </button>
  );

  const handleAlertChange = useCallback((alertKey, field, value) => {
    setAlerts((prev) => ({
      ...prev,
      [alertKey]: { ...prev[alertKey], [field]: value },
    }));
  }, []);

  const handleUserSelect = useCallback((id) => {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, selected: !u.selected } : u)));
  }, []);

  const handleSelectAllUsers = useCallback(() => {
    const next = !selectAllUsers;
    setUsers((prev) => prev.map((u) => ({ ...u, selected: next })));
  }, [selectAllUsers]);

  const rememberTenant = (data) => {
    if (!data) return;
    const bits = [data.tenantDb, data.finYear, data.poolDefaultDb].filter(Boolean);
    if (bits.length) setTenantInfo(bits.join(" | "));
  };

  const loadPageData = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/data`, {
      params: tenantParams,
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load service expiry data");
    setAlerts(data?.alerts ? { ...defaultAlerts, ...data.alerts } : defaultAlerts);
    setUsers(Array.isArray(data?.users) ? data.users : []);
    rememberTenant(data);
  }, [tenantParams, commonHeaders]);

  const loadAll = useCallback(async () => {
    try {
      setPageLoading(true);

      if (!loginUserId && !userId) {
        throw new Error("loginUserId missing in localStorage / sessionStorage");
      }
      if (!finYear && !finYearId) {
        throw new Error("finYear / finYearId missing. Tenant inventory DB cannot be selected.");
      }

      await loadPageData();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load");
    } finally {
      setPageLoading(false);
    }
  }, [loadPageData, loginUserId, userId, finYear, finYearId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleClear = () => {
    setAlerts(defaultAlerts);
    setUsers((prev) => prev.map((u) => ({ ...u, selected: false })));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/save`,
        {
          loginUserId: toInt(loginUserId || userId, 0),
          finYear,
          finYearId,
          alerts,
          selectedUserIds: users.filter((u) => !!u.selected).map((u) => u.id),
        },
        {
          params: tenantParams,
          headers: commonHeaders,
        }
      );
      if (!data?.success) throw new Error(data?.message || "Failed to save");
      toast.success(data?.message || "Saved Successfully");
      rememberTenant(data);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const renderAlertRows = (list, valueKey) =>
    list.map((service, idx) => (
      <tr
        key={service.key}
        className={`${
          idx % 2 === 0
            ? isDark
              ? "bg-gray-800"
              : "bg-white"
            : isDark
            ? "bg-gray-750"
            : "bg-gray-50"
        } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
      >
        <td className="px-6 py-3 text-sm font-medium">{service.label}</td>
        <td className="px-6 py-3">
          <div className="flex justify-center">
            <ToggleButton
              enabled={!!alerts[service.key]?.enabled}
              onChange={(value) => handleAlertChange(service.key, "enabled", value)}
              disabled={busy}
            />
          </div>
        </td>
        <td className="px-6 py-3">
          <input
            type="number"
            value={alerts[service.key]?.[valueKey] ?? 0}
            onChange={(e) => handleAlertChange(service.key, valueKey, toInt(e.target.value, 0))}
            className={`w-24 text-center mx-auto ${inputClass}`}
            disabled={!alerts[service.key]?.enabled || busy}
          />
        </td>
        {["software", "sms", "email"].map((field) => (
          <td key={field} className="px-6 py-3">
            <div className="flex justify-center">
              <Checkbox
                checked={!!alerts[service.key]?.[field]}
                onChange={() => handleAlertChange(service.key, field, !alerts[service.key]?.[field])}
                disabled={busy}
              />
            </div>
          </td>
        ))}
      </tr>
    ));

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SERVICE EXPIRY ALERT</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Service Expiry Alert Settings
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {pageLoading
                  ? "Loading..."
                  : tenantInfo
                  ? `Tenant: ${tenantInfo}`
                  : finYear
                  ? `Year: ${finYear}`
                  : "Year not selected"}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft /> Back
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={busy}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${
                  busy ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass}`}
              >
                <FaSave />
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>

          <Section title="Own Vehicle Expiry Alert" isDark={isDark}>
            <div className="overflow-x-auto rounded-lg border">
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    {["Service", "Enable", "Days", "Software", "SMS", "Email"].map((h) => (
                      <th key={h} className="px-6 py-3 text-sm font-semibold text-center first:text-left">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {renderAlertRows(ownVehicleRows, "days")}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Periodic Expiry Alert" isDark={isDark}>
            <div className="overflow-x-auto rounded-lg border">
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    {["Service", "Enable", "Value", "Software", "SMS", "Email"].map((h) => (
                      <th key={h} className="px-6 py-3 text-sm font-semibold text-center first:text-left">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {renderAlertRows(periodicRows, "value")}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Alert Recipients" isDark={isDark}>
            <div className="flex items-center justify-end gap-3 mb-4">
              <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Select All
              </span>
              <Checkbox
                checked={selectAllUsers}
                onChange={handleSelectAllUsers}
                disabled={busy}
              />
            </div>
            <div className="overflow-x-auto rounded-lg border max-h-96 overflow-y-auto">
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white sticky top-0`}>
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold">User Name</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Mobile No</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold w-16">Select</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-sm opacity-70">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user, idx) => (
                      <tr
                        key={user.id}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className="px-6 py-3 text-sm">{toStr(user.userName)}</td>
                        <td className="px-6 py-3 text-sm">{toStr(user.mobileNo)}</td>
                        <td className="px-6 py-3 text-sm break-all">{toStr(user.email)}</td>
                        <td className="px-6 py-3">
                          <div className="flex justify-center">
                            <Checkbox
                              checked={!!user.selected}
                              onChange={() => handleUserSelect(user.id)}
                              disabled={busy}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Section>

          <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClear}
                disabled={busy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <FaTimes /> Clear
              </button>
              <button
                type="button"
                onClick={loadAll}
                disabled={busy}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                Reset
              </button>
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                busy ? "opacity-60 cursor-not-allowed" : ""
              } ${buttonClass} ${hoverClass}`}
            >
              <FaSave />
              Save Alert Settings
            </button>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

function Section({ title, isDark, children }) {
  return (
    <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
        {title}
      </h3>
      {children}
    </div>
  );
}

export default ServiceExpiryAlert;